import { defineStore, acceptHMRUpdate } from 'pinia'
import { tnApi } from '@/api'
import { useNotificationStore } from '@/pinia/NotificationStore'
import { showAxiosErrorDialog } from '@/util/dialogs.js'

const showErrorDetails = (notification = null) => {
  console.log('showErrorDetails', notification)
  if (notification !== null) {
    showAxiosErrorDialog({
      errorObject: JSON.stringify(notification?.extraPayload)
    })
  }
}

export const useInquiryStore = defineStore('inquiry', {
  state: () => ({
    inquiries: [],
    meta: {},
    inquiry: {},
    stats: {},
    inquiryUndo: {},
    pointsMeta: {},
    isPointsLoading: false,
    points: [],
    isLoading: false,
    groups: [],
    templates: [],
    legacyTemplates: [],
    selectedItems: [],
    orgAnswer: {},
    extras: null,
    answerTemplate: {
      id: null,
      subject: '',
      body: '',
      template_id: null,
      cc: false,
      is_draft: false,
      is_rejection: false,
      error: ''
    },
    batchActions: [
      {
        id: 2,
        name: 'annehmen',
        success: 'angenommen',
        types: ['pool', 'community']
      },
      {
        id: 0,
        name: 'als ungelesen markieren',
        success: 'als ungelesen markiert',
        types: ['direct', 'pool', 'community']
      },
      {
        id: 1,
        name: 'als gelesen markieren',
        success: 'als gelesen markiert',
        types: ['direct', 'pool', 'community']
      },
      {
        id: 9,
        name: 'in den Papierkorb verschieben',
        success: 'in den Papierkorb verschoben',
        types: ['direct', 'pool', 'community']
      },
      {
        id: 91,
        name: 'wiederherstellen',
        success: 'wiederhergestellt',
        types: ['trashed']
      }
      /*
      {
        id: 92,
        name: 'endgültig löschen',
        success: 'endgültig gelöscht',
        types: ['trashed']
      }
      */
    ],
    answer: {},
    statuses: [
      {
        id: 0,
        icon: 'mail-filled',
        color: 'blue',
        description: 'ungelesen'
      },
      {
        id: 1,
        icon: 'mail-opened',
        color: 'gray',
        description: 'gelesen'
      },
      {
        id: 2,
        icon: 'checks',
        color: 'blue',
        description: 'angenommen'
      },
      {
        id: 3,
        icon: 'send',
        color: 'green',
        description: 'geantwortet'
      },
      {
        id: 4,
        icon: 'hourglass',
        color: 'gray',
        description: 'wartet auf Tauschzustimmung'
      },
      {
        id: 5,
        icon: 'share',
        color: 'green',
        description: 'getauscht'
      },
      {
        id: 6,
        icon: 'calendar-filled',
        color: 'green',
        description: 'gebucht'
      },
      {
        id: 9,
        icon: 'trash-x',
        color: 'gray',
        description: 'Papierkorb'
      },
      {
        id: 98,
        icon: 'alert-triangle-filled',
        color: 'red',
        description: 'Sendeprobleme'
      },
      {
        id: 99,
        icon: 'question-circle',
        color: 'red',
        description: null
      }
    ]
  }),
  getters: {
    answerChanged: (state) => JSON.stringify(state.answer) !== JSON.stringify(state.orgAnswer),
    answerTemplates: (state) => state.legacyTemplates.filter(item => parseInt(item.type) === 1),
    hasFailedAnswers: (state) => {
      const failed = state.inquiries.filter(item => item.is_failed === true)
      return failed.length > 0
    }
  },
  actions: {
    getStatus (inquiry) {
      const statusCode = this.getStatusCode(inquiry)
      const status = this.statuses.find(item => item.id === statusCode)
      if (status) {
        return status
      }
      const unknownStatus = this.statuses.find(item => item.id === 99)
      unknownStatus.description = `unbekannt (${statusCode})`
      return unknownStatus
    },
    getStatusCode (inquiry) {
      if (inquiry.booking !== null) {
        return 6
      }

      if (inquiry.is_failed) {
        return 98
      }

      if (inquiry.userStatus?.trashed_at) {
        return 9
      }

      if (inquiry.user_status === 4) {
        return inquiry.status_opt_in === 2 ? 5 : 4
      }
      return inquiry.user_status
    },
    answerUndo () {
      this.orgAnswer = Object.assign({}, this.answer)
    },
    createRejection () {
      const templateType = this.inquiry.status_opt_in === 2 ? 3 : 2
      let template = this.legacyTemplates.find(item => item.type === templateType && item.lang === this.inquiry.contact.language_code)
      console.log(template)
      if (!template) {
        template = this.legacyTemplates.find(item => item.type === templateType && item.lang === 'de')
      }
      const id = template ? template.id : 0
      this.createAnswer(id, true)
    },
    async saveAndSendAnswer (type, isDraft, shouldSend = false) {
      this.answer.is_draft = isDraft
      try {
        const {
          inquiriesSaveAndSendAnswer
        } = tnApi()
        const { inquiry } = await inquiriesSaveAndSendAnswer(type, this.inquiry.hid, { answer: this.answer }, shouldSend)
        const store = useInquiryStore()
        store.$patch((state) => {
          state.inquiry = inquiry
        })
      } catch (error) {
        console.log(error)
        this.answer.is_draft = true
        this.answer.error = error.response.data.message
        if (shouldSend) {
          const notificationStore = useNotificationStore()
          notificationStore.addClientNotification(
            {
              title: 'Deine Antwort konnte nicht gesendet werden',
              text: 'Deine Antwort konnte aufgrund eines Fehlers nicht gesendet werden.',
              variant: 'error',
              button: 'Details anzeigen',
              action: showErrorDetails,
              timeout: -1,
              extraPayload: error.response.data
            }
          )
        }
        this.editAnswer()
        return await Promise.reject(error)
      }
    },
    async resendAnswer (type, answerId) {
      const {
        inquiriesResendAnswer
      } = tnApi()
      try {
        const { inquiry } = await inquiriesResendAnswer(type, this.inquiry.hid, answerId)
        this.inquiry = inquiry
      } catch (error) {
        console.log(error)
        const notificationStore = useNotificationStore()
        notificationStore.addClientNotification(
          {
            title: 'Deine Antwort konnte nicht gesendet werden',
            text: 'Deine Antwort konnte aufgrund eines Fehlers nicht gesendet werden.',
            variant: 'error',
            button: 'Details anzeigen',
            action: showErrorDetails,
            timeout: -1,
            extraPayload: error.response.data
          }
        )
      }
    },
    editAnswer () {
      if (this.inquiry.mails[0]?.is_draft) {
        this.answer = Object.assign({}, this.inquiry.mails[0])
        this.answerUndo()
      }
    },
    async discardAnswer (type) {
      const {
        inquiriesDiscardAnswer
      } = tnApi()
      await inquiriesDiscardAnswer(type, this.inquiry.hid)
      this.inquiry.mails = []
      this.resetAnswer()
      this.answerUndo()
    },
    createAnswer (tid, isRejection = false) {
      this.answer = Object.assign({}, this.answerTemplate)
      if (tid === 0) {
        this.answer.template_id = 0
        this.answer.subject = ''
        this.answer.body = ''
        this.answer.is_rejection = isRejection
      }
      const template = this.legacyTemplates.find(item => item.id === tid)
      if (template) {
        this.answer.template_id = template.id
        this.answer.subject = template.subject
        this.answer.is_rejection = isRejection
        this.answer.body = template.body
      }
      this.answerUndo()
    },
    resetAnswer () {
      this.answer = Object.assign({}, this.answerTemplate)
      this.answerUndo()
    },
    async load (type, page) {
      this.isLoading = true
      try {
        const {
          inquiriesLoad
        } = tnApi()
        const { inquiries, meta, stats } = await inquiriesLoad(type, page)
        this.inquiries = inquiries
        this.meta = meta
        this.stats = stats
      } catch (error) {
        console.error(error)
      }

      this.isLoading = false
    },
    async loadPoints (page) {
      this.isPointsLoading = true
      const {
        inquiriesPoints
      } = tnApi()
      const { points, meta, stats } = await inquiriesPoints(page)
      this.points = points.data
      this.pointsMeta = meta
      this.stats = stats
      this.isPointsLoading = false
    },
    async batch (type, action) {
      const {
        inquiriesBatch
      } = tnApi()
      const { statuses, stats, removedIds } = await inquiriesBatch(type, action, {
        ids: this.selectedItems
      })

      const idsToDelete = removedIds || []

      let inquiries = this.inquiries
      let selectedItems = this.selectedItems

      for (const status of statuses) {
        if (status.status_id !== 9) {
          const index = this.inquiries.findIndex(item => item.id === status.inquiry_id)
          if (index > -1) {
            inquiries[index].user_status = status.status_id
            inquiries[index].userStatus = status
          }
          if (type === 'trashed' || status.trashed_at) {
            idsToDelete.push(status.inquiry_id)
          }
        } else {
          idsToDelete.push(status.inquiry_id)
        }
        selectedItems = selectedItems.filter(item => item !== parseInt(status.inquiry_id))
      }

      inquiries = inquiries.filter(item => !idsToDelete.includes(item.id))

      const store = useInquiryStore()
      store.$patch((state) => {
        state.inquiries = inquiries
        this.selectedItems = selectedItems
        state.stats = stats
        state.hasChanged = true
      })

      return Promise.resolve(statuses.length)
    },
    async find (id, type = 'direct') {
      if (id === undefined) return
      this.isLoading = true
      this.answer.template_id = 0
      const {
        inquiriesFind
      } = tnApi()
      const { inquiry, legacyTemplates } = await inquiriesFind(id, type)
      this.legacyTemplates = legacyTemplates
      this.isLoading = false
      this.inquiry = inquiry
    },
    async update (id, type = 'direct') {
      const store = useInquiryStore()
      if (id === undefined) return
      // const index = this.inquiries.findIndex(item => item.hid === id)
      const {
        inquiriesFind
      } = tnApi()
      const { inquiry } = await inquiriesFind(id, type)
      store.$patch((state) => {
        state.inquiry = inquiry
        state.hasChanged = true
      })
    },
    async status (hid, status, type = 'direct') {
      try {
        const {
          inquiriesSetStatus
        } = tnApi()
        const { inquiry, stats } = await inquiriesSetStatus(hid, status, type.value)

        if (inquiry && status !== 9) {
          let inquiries = this.inquiries
          const index = inquiries.findIndex(item => item.hid === hid)
          if (index > -1) {
            inquiries[index] = inquiry
          }

          if (type === 'starred' && !inquiry.is_starred) {
            inquiries = inquiries.filter(item => item.hid !== hid)
          }

          const store = useInquiryStore()
          store.$patch((state) => {
            state.inquiries = inquiries
            state.inquiry = inquiry
            state.stats = stats
            state.hasChanged = true
          })
        }
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async setStats (stats) {
      const store = useInquiryStore()
      store.$patch((state) => {
        state.stats = stats
      })
    },
    async remove (id, aid, module = 'accommodations') {
    },
    async save (id, aid, module = 'accommodations') {
    },
    async edit (id = null, aid = null) {
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInquiryStore, import.meta.hot))
}
